import Modal from '@Common/Modal';
import { useDiffMerge } from '@Context/prompt-toolbox/DiffMergeContext';
import { usePromptToolboxContext } from '@Context/prompt-toolbox/PromptToolboxContext';
import { useUtilityContext } from '@Context/prompt-toolbox/UtilityContext';
import { UserPromptVersion } from '@Models/types';
import {
  ArrowDown,
  ArrowsOutCardinal,
  CaretDoubleRight,
  CaretDown,
  CaretRight,
  ClockCountdown,
  File,
  FloppyDiskBack,
  Robot,
  SquaresFour,
} from '@phosphor-icons/react';
import SaveVersion from '../modals/SaveVersion';
import ResetVersion from '../modals/ResetVersion';
import { BounceLoader, MoonLoader } from 'react-spinners';
import colors from '@Common/assets/colors';
import { useEffect, useRef, useState } from 'react';
import useWebSocket, { ReadyState } from 'react-use-websocket';
import { GPT_WSS } from '@Env/variables';
import { toGPTMessage } from '@GPT/views/chat/websockets';
import { Tooltip } from 'react-tooltip';
import ReactiveIcon from '@Common/ReactiveIcon';
import Markdown from 'react-markdown';
import { useAuth } from '@Context/CognitoAuthProvider';
import showToast from '@Common/toast/showToast';
export interface GPTMessage {
  sender: 'you' | 'gpt';
  message: string;
  dateSent: Date;
}

const MAX_RECONNECT = 3;

const NewPrompt = () => {
  const { setNewPromptData, loaders, modals, setModal, setLoader } =
    useUtilityContext();
  const { selectedPromptVersion, addNewVersion, userPrompts } =
    usePromptToolboxContext();
  // Include the shared diff state from the context:
  const { edited, setEdited, diffParts, hasChanges, showDiff } = useDiffMerge();
  const [message, setMessage] = useState<GPTMessage | null>(null);
  const [isConnected, setIsConnected] = useState(false);
  const [isReconnecting, setIsReconnecting] = useState(false);
  const [WssAuthError, setWssAuthError] = useState(false);
  const { user, logout, refreshIfExpired } = useAuth();
  const [isGenerating, setIsGenerating] = useState(false);
  const [isFileReferenceExpanded, setIsFileReferenceExpanded] = useState(false);
  const reconnectOnAbnormalClose = useRef(0);

  //We need the reference key from S3. So this function removes the reference key from S3 to hide sensitive info from the file context.
  const hideSystemReferences = (content: string): string => {
    if (!content) return content;

    return content
      .replace(/\[image:[^\]]+\]/g, '')
      .replace(/\[pdf:[^\]]+\]/g, '')
      .replace(/\[text:[^\]]+\]/g, '')
      .replace(/\[document:[^\]]+\]/g, '')
      .replace(/\n\n+/g, '\n\n')
      .trim();
  };

  const { sendMessage, lastMessage, readyState } = useWebSocket(GPT_WSS, {
    onOpen: async () => {
      try {
        await refreshIfExpired(localStorage.getItem('accessToken'));
      } catch {
        logout();
      }
      console.log('[ socket: OPENED ]');
      sendMessage(
        JSON.stringify({
          action: 'sendmsgtest',
          token: localStorage.getItem('accessToken'),
        })
      );
    },
    onClose: (event) => {
      console.log('[ socket: CLOSED ]', event.code, event.reason);
      if (event.code === 1006) {
        reconnectOnAbnormalClose.current += 1;
      }
      if (
        event.code === 1008 ||
        reconnectOnAbnormalClose.current >= MAX_RECONNECT ||
        WssAuthError
      ) {
        showToast.error(
          'Session invalid. Please log in again to continue using the app.',
          'wss-auth-error'
        );

        logout();
      }
    },

    shouldReconnect: () =>
      true && reconnectOnAbnormalClose.current < MAX_RECONNECT,
    reconnectAttempts: MAX_RECONNECT,
  });
  const [processing, setProcessing] = useState(false);
  const [lastProcessedChunk, setLastProcessedChunk] = useState<string>('');

  useEffect(() => {
    if (readyState === ReadyState.OPEN) {
      setIsConnected(true);
      setIsReconnecting(false);
    } else if (readyState === ReadyState.CLOSED && isConnected) {
      console.log('Socket Disconnected');
      setIsReconnecting(true);
    } else {
      setIsReconnecting(false);
    }
  }, [readyState, isConnected]);

  useEffect(() => {
    setMessage(null);
  }, [selectedPromptVersion]);

  useEffect(() => {
    if (lastMessage !== null) {
      try {
        const dataObj = JSON.parse(lastMessage.data);
        const { action, type, message: streamMessage } = dataObj;
        const messageObj =
          typeof streamMessage === 'string' ? JSON.parse(streamMessage) : '';

        if (action === 'chat' && typeof messageObj === 'object') {
          const dateSent = messageObj.dateTime
            ? new Date(messageObj.dateTime)
            : new Date();

          const responseMessage: GPTMessage = {
            sender: 'gpt',
            message: messageObj.response,
            dateSent: dateSent,
          };

          setMessage(responseMessage);
          // setIsProcessing(false);
          setLoader('gptNew', false);
          setIsGenerating(false);
        } else if (action === 'processing') {
          // setisprocessing(messageObj.loading);
          setLoader('gptNew', messageObj.loading);
        } else if (action === 'error') {
          if (type === 'auth-error') {
            setWssAuthError(true);
          } else if (type !== 'auth-timeout') {
            const responseMessage: GPTMessage = {
              sender: 'gpt',
              message: `ERROR‼️ ${messageObj.details}\n Please contact your system administrator.`,
              dateSent: new Date(),
            };
            setLoader('gptAdd', false);
            setMessage(responseMessage);
            setProcessing(false);
          }
        } else if (action === 'stream') {
          const chunkContent = messageObj.response;

          // Prevent duplicate chunk processing
          if (chunkContent === lastProcessedChunk) {
            return;
          }
          setLastProcessedChunk(chunkContent);

          setProcessing(false);
          setMessage((currentMessage) => {
            if (!currentMessage) {
              const dateSent = messageObj.dateTime
                ? new Date(messageObj.dateTime)
                : new Date();

              return {
                sender: 'gpt',
                message: chunkContent,
                dateSent: dateSent,
              };
            } else {
              return {
                ...currentMessage,
                message: currentMessage.message + chunkContent,
              };
            }
          });
        } else if (action === 'stream_end') {
          setProcessing(false);
          setLoader('gptNew', false);
          setIsGenerating(false);
        }
      } catch (err) {
        console.error('Error parsing message data:', err);
        setIsGenerating(false);
      }
    }
  }, [lastMessage, lastProcessedChunk, setLoader]);

  const handleSend = () => {
    setIsGenerating(true);
    try {
      if (!setEdited || edited.trim() === '') {
        showToast.error('You cannot send empty prompts.');
        return;
      }

      // Check if the parent prompt has processed file content
      const parentPrompt = userPrompts.find(
        (p) => p.id === selectedPromptVersion?.id
      );
      let messageToSend = edited;

      if (parentPrompt?.processedFilePrompt) {
        // Combine the user's new prompt with the existing processed file content
        messageToSend = `${edited}\n\n=== REFERENCE FILES ===\n${parentPrompt.processedFilePrompt}`;

        showToast.info(
          'Including processed file content from parent prompt for context.'
        );
      }

      setLoader('gptNew', true);
      setProcessing(true);
      setMessage(null);
      setLastProcessedChunk('');
      sendMessage(toGPTMessage(messageToSend, null, 'onetime'));
    } catch (error) {
      console.error('Error sending message:', error);
      setIsGenerating(false);
      setLoader('gptNew', false);
    }
  };

  const disable = !selectedPromptVersion || !hasChanges() || showDiff;
  const canSave = !!message?.message && !loaders.gptNew && !loaders.newVersion;

  const getHighestVersion = (): string => {
    return selectedPromptVersion!.versions.sort((a, b) => {
      const versionA = a.split('.').map(Number);
      const versionB = b.split('.').map(Number);
      for (let i = 0; i < Math.max(versionA.length, versionB.length); i++) {
        if ((versionA[i] || 0) > (versionB[i] || 0)) return -1;
        if ((versionA[i] || 0) < (versionB[i] || 0)) return 1;
      }
      return 0;
    })[0] as string;
  };

  const getNextVersion = () => {
    const highestVersion = getHighestVersion();
    const [major, minor] = String(highestVersion).split('.').map(Number);
    return `${major}.${minor + 1}`;
  };

  const handleNewVersion = async () => {
    setModal('saveVersion', false);
    const input: UserPromptVersion = {
      gpt_result: message ? message.message : '',
      prompt: edited,
      updatedBy: String(user?.profile?.name),
      version: getNextVersion(),
      timestamp: String(Date.now()),
    };

    await addNewVersion(input);
  };

  const firstDivRef = useRef<HTMLDivElement>(null);
  const secondDivRef = useRef<HTMLTextAreaElement>(null);

  const handleScrollSecond = (scroll: React.UIEvent<HTMLTextAreaElement>) => {
    if (firstDivRef.current) {
      firstDivRef.current.scrollTop = scroll.currentTarget.scrollTop;
    }
  };

  //will update new prompt/response context state
  useEffect(() => {
    if (message?.message) {
      setNewPromptData((prev) => ({
        ...prev,
        prompt: edited,
        response: message.message,
      }));
    } else {
      //will run when only the prompt is changed
      setNewPromptData((prev) => ({
        ...prev,
        prompt: edited,
      }));
    }
  }, [message?.message, edited, setNewPromptData]);

  return (
    <div className="flex h-full w-full min-w-64 flex-col gap-y-3 rounded-lg border-2 border-gray-200 p-3">
      {!selectedPromptVersion && (
        <span className="flex h-full flex-col items-center justify-center text-neutral-300">
          <SquaresFour size={150} weight="bold" />
          <label className="text-xs font-medium tracking-wider text-neutral-500">
            Select a prompt first
          </label>
        </span>
      )}

      {isReconnecting && (
        <div className="absolute inset-0 z-10 flex flex-col items-center justify-center gap-y-3 rounded-xl bg-black opacity-90">
          <div className="flex flex-col items-center gap-y-2 rounded-md p-4">
            <MoonLoader color="white" />
            <label className="text-4xl font-bold text-white">
              Chat Session Ended
            </label>
            <label className="animate-pulse text-xl font-extrabold text-primary-500">
              Attempting to reconnect
            </label>
          </div>
        </div>
      )}

      {selectedPromptVersion && (
        <>
          {/* DETAILS */}
          <div className="flex w-full flex-col border-gray-600 bg-white font-inter">
            <div className="flex w-full flex-row items-center justify-between">
              <div className="flex w-full flex-row items-center justify-start gap-x-2 text-sm">
                <label>Version {selectedPromptVersion.version.version}</label>
                <CaretDoubleRight size={20} />
                <label
                  data-testid="label-version"
                  className={`${
                    loaders.newVersion
                      ? 'animate-pulse bg-brandSuccess-500'
                      : 'bg-primary-300'
                  } rounded-lg px-3 py-0.5 font-semibold text-white`}
                >
                  Version {getNextVersion()}
                </label>
              </div>
              <div className="flex flex-row gap-x-1 text-sm font-medium">
                {loaders.newVersion ? (
                  <div className="flex flex-row items-center gap-x-2">
                    <label className="animate-pulse whitespace-nowrap text-sm tracking-wide">
                      Saving
                    </label>
                    <BounceLoader size={20} color={colors.brandWarning[500]} />
                  </div>
                ) : (
                  <>
                    <button
                      data-testid="save-button"
                      type="button"
                      disabled={!canSave}
                      data-tooltip-id="reset"
                      data-tooltip-content="Save"
                      data-tooltip-place="left"
                      onClick={() => setModal('saveVersion', true)}
                      aria-label="Save version"
                      className={`${!canSave ? 'pointer-events-none cursor-not-allowed' : ''}`}
                    >
                      <ReactiveIcon
                        Icon={FloppyDiskBack}
                        className={`${canSave ? 'text-primary-700' : 'text-gray-500'}`}
                        clicked={modals.saveVersion}
                      />
                    </button>
                    <button
                      data-testid="reset-button"
                      data-tooltip-id="reset"
                      data-tooltip-content="Reset"
                      data-tooltip-place="left"
                      disabled={disable}
                      onClick={() => setModal('resetVersion', true)}
                    >
                      <ReactiveIcon
                        Icon={ClockCountdown}
                        className={`${!disable ? 'text-brandRed-500' : 'text-gray-500'}`}
                        clicked={modals.resetVersion}
                      />
                    </button>
                    <Tooltip
                      id="reset"
                      style={{
                        color: 'white',
                        background: colors.primary[700],
                        borderRadius: '0.5rem',
                      }}
                      className="font-medium"
                    />
                  </>
                )}
              </div>
            </div>
          </div>

          {/* PROMPT */}
          <div className="group relative flex h-1/2 w-full flex-col border-2 border-gray-300">
            {/* File Reference Indicator */}
            {(() => {
              const parentPrompt = userPrompts.find(
                (p) => p.id === selectedPromptVersion?.id
              );
              return parentPrompt?.processedFilePrompt ? (
                <div className="border-b border-blue-200 bg-blue-50">
                  {/* Header - Always visible */}
                  <div
                    className="flex cursor-pointer items-center gap-2 px-3 py-2 text-xs text-blue-700 hover:bg-blue-100"
                    onClick={() =>
                      setIsFileReferenceExpanded(!isFileReferenceExpanded)
                    }
                  >
                    {isFileReferenceExpanded ? (
                      <CaretDown size={12} className="text-blue-500" />
                    ) : (
                      <CaretRight size={12} className="text-blue-500" />
                    )}
                    <File size={12} className="text-blue-500" />
                    <span>
                      This prompt references processed file content from the
                      initial prompt
                    </span>
                    <span className="ml-auto text-blue-500">
                      {isFileReferenceExpanded ? 'Collapse' : 'View files'}
                    </span>
                  </div>

                  {/* Expandable content */}
                  {isFileReferenceExpanded && (
                    <div className="bg-blue-25 max-h-40 overflow-y-auto border-t border-blue-200 px-3 py-2">
                      <div className="mb-2 text-xs font-medium text-blue-600">
                        Referenced File Content:
                      </div>
                      <pre className="whitespace-pre-wrap rounded border border-blue-200 bg-white p-2 font-mono text-xs text-blue-800">
                        {hideSystemReferences(parentPrompt.processedFilePrompt)}
                      </pre>
                    </div>
                  )}
                </div>
              ) : null;
            })()}

            <div className="h-full w-full pr-1">
              <div className="relative h-full w-full overflow-hidden">
                {/* Conditionally render the highlighted diff layer */}
                {showDiff && (
                  <div
                    ref={firstDivRef}
                    className="scrollbar-hide absolute inset-0 h-full w-full overflow-auto whitespace-pre-wrap break-words p-4"
                    aria-hidden="true"
                  >
                    {diffParts.map((part, index) => (
                      <span
                        key={index}
                        className={
                          part.removed
                            ? 'hidden'
                            : part.added
                              ? 'bg-green-300'
                              : ''
                        }
                      >
                        {part.value}
                      </span>
                    ))}
                  </div>
                )}

                {/* Editable textarea */}
                <textarea
                  data-testid="selected-text-area"
                  spellCheck={false}
                  ref={secondDivRef}
                  onScroll={handleScrollSecond}
                  className={`focus:ring-none custom-scroll relative inset-0 h-full w-full resize-none overflow-auto rounded-lg p-4 caret-black outline-none ${
                    showDiff
                      ? 'bg-transparent text-transparent'
                      : 'bg-white text-black'
                  }`}
                  value={edited}
                  onChange={(e) => {
                    setEdited(e.target.value);
                  }}
                />
              </div>
            </div>

            <div className="absolute bottom-2 right-2 flex flex-col gap-y-2 opacity-0 transition-opacity duration-300 group-hover:opacity-100">
              <button
                data-tooltip-id="expand"
                data-tooltip-content="Expand"
                data-tooltip-place="left"
                className="translate-y-4 transform rounded-full bg-primary-700 p-2 transition-transform duration-300 hover:shadow-xl group-hover:translate-y-0"
                onClick={() => setModal('expandNewVersionPrompt', true)}
              >
                <ArrowsOutCardinal
                  size={20}
                  weight="bold"
                  className="text-white"
                />
              </button>
              <Tooltip
                id="expand"
                style={{
                  color: 'white',
                  background: colors.primary[700],
                  borderRadius: '0.5rem',
                }}
                className="font-medium"
              />
              <Tooltip
                id="generate"
                style={{
                  color: 'white',
                  background: colors.primary[700],
                  borderRadius: '0.5rem',
                }}
                className="font-medium"
              />
              {!isConnected || processing ? (
                <div className="translate-y-4 transform rounded-full bg-primary-700 p-2 transition-transform duration-300 hover:shadow-xl group-hover:translate-y-0">
                  <BounceLoader color={colors.brandWarning[300]} size={20} />
                </div>
              ) : (
                <button
                  disabled={disable}
                  data-tooltip-id="generate"
                  data-tooltip-content="Generate"
                  data-tooltip-place="left"
                  data-testid="generate-button"
                  onClick={handleSend}
                  className="translate-y-4 transform rounded-full bg-primary-700 p-2 transition-transform duration-300 hover:shadow-xl disabled:bg-gray-300 group-hover:translate-y-0"
                >
                  <ArrowDown size={20} weight="bold" className="text-white" />
                </button>
              )}
            </div>
          </div>

          {/* OUTPUT */}
          <div className="group relative flex h-1/2 w-full flex-col border-2 border-gray-300">
            {!message && !loaders.gptNew && (
              <span className="flex h-full flex-col items-center justify-center text-neutral-300">
                <Robot size={150} />
                <label className="text-xs font-medium tracking-wider text-neutral-500">
                  No AI Response
                </label>
              </span>
            )}
            {processing && (
              <span className="flex h-full flex-col items-center justify-center gap-y-3 text-neutral-300">
                <MoonLoader size={100} />
                <label className="animate-pulse text-xs font-medium tracking-wider text-neutral-500">
                  Generating Response
                </label>
              </span>
            )}
            {message && message.message && (
              <div className="flex h-full w-full flex-grow border-2 border-gray-300 pr-1">
                <Markdown className="markdown animate-typing custom-scroll h-full w-full resize-none overflow-y-auto p-4 text-sm focus:outline-none">
                  {message.message}
                </Markdown>
              </div>
            )}
            {message && message.message && !loaders.gptNew && (
              <div className="absolute bottom-2 right-2 opacity-0 transition-opacity duration-300 group-hover:opacity-100">
                <button
                  disabled={isGenerating}
                  data-tooltip-id="expand-res"
                  data-tooltip-content="Expand"
                  data-tooltip-place="left"
                  className="translate-y-4 transform rounded-full bg-primary-700 p-2 transition-transform duration-300 hover:shadow-xl group-hover:translate-y-0"
                  onClick={() => setModal('expandNewGPTResult', true)}
                >
                  <ArrowsOutCardinal
                    size={20}
                    weight="bold"
                    className="text-white"
                  />
                </button>
                <Tooltip
                  id="expand-res"
                  style={{
                    color: 'white',
                    background: colors.primary[700],
                    borderRadius: '0.5rem',
                  }}
                  className="font-medium"
                />
              </div>
            )}
          </div>
        </>
      )}
      <Modal
        isOpen={modals.saveVersion}
        onClose={() => setModal('saveVersion', false)}
        data-testid="new-prompt-version-modal"
        title="New Prompt Version"
      >
        <SaveVersion onAdd={handleNewVersion} />
      </Modal>
      <Modal
        isOpen={modals.resetVersion}
        onClose={() => setModal('resetVersion', false)}
        title="Reset Prompt Version"
      >
        <ResetVersion />
      </Modal>
    </div>
  );
};

export default NewPrompt;
